import _fetch from 'isomorphic-fetch';

import { generateGuid } from '../util/generate-guid';
import { getApiEndpoint } from '../util/get-api-endpoint';
import { JWT } from '../util';
import { addToBatch } from '../event-hub';
import { trackEvent } from '../tracking-tool/app-insights';
import {
    RequestResponse,
    BaseRequestPayload,
    DeletePayload,
    PatchPayload,
    PutPayload,
    PostPayload,
    GetPayload,
} from './types';

type ApiRequestPayload = BaseRequestPayload & {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
};

/**
 * A wrapper around the browser fetch API
 */
export class API {
    private baseURL: string;
    private headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Tenant: '',
        Authorization: 'Bearer null',
    };
    private emails: string[];

    /**
     * Creates an API wrapper
     *
     * @param {string} baseURL
     */
    constructor(baseURL: string) {
        this.baseURL = baseURL;
    }

    /**
     * Stringify the request payload
     *
     * @param {ApiRequestPayload} payload - The request payload
     *
     * @return {String | undefined}
     */
    private stringifyBody({
        body,
        method,
    }: Pick<ApiRequestPayload, 'body' | 'method'>): string | undefined {
        if (['POST', 'PUT', 'PATCH'].includes(method) && body) {
            return JSON.stringify(body);
        }
        return undefined;
    }

    /**
     * Make an API request to the server
     *
     * @param {ApiRequestPayload} payload - The request payload
     *
     * @return {Promise}
     */
    private async request<T, S = never>({
        url,
        body,
        headers = {},
        method,
        signal,
        isJson,
    }: ApiRequestPayload): Promise<RequestResponse<T, S> | string> {
        const apiURL = `${this.baseURL}${url}`;
        this.headers.correlation_id = generateGuid();

        try {
            const emails = this.emails;
            const { location } = window;
            const { Tenant, correlation_id } = this.headers;
            const service = url.substring(1).split('/')[0];
            addToBatch({
                message: 'fetch',
                emails,
                tenant: Tenant,
                correlationId: correlation_id,
                service,
                url,
                view: location.pathname,
                method,
                body,
            });
        } catch (error) {
            trackEvent('EVENT_HUB_XHR_ERROR', error);
        }

        const response = await _fetch(apiURL, {
            method,
            mode: 'cors',
            headers: {
                ...this.headers,
                ...headers,
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: this.stringifyBody({ body, method }),
            signal,
        });

        if (isJson) return response.json() as Promise<RequestResponse<T, S>>;

        return response.text();
    }

    /**
     * Make an API stream request to the server
     *
     * @param {ApiRequestPayload} payload - The stream request payload
     *
     * @return {Promise}
     */
    public async stream({
        url,
        headers,
        signal,
        body,
        method,
    }: Omit<ApiRequestPayload, 'isJson'>): Promise<string> {
        return this.request({
            url,
            headers,
            method,
            body,
            ...(signal && { signal }),
            isJson: false,
        }) as Promise<string>;
    }

    /**
     * Make a JSON request to the server
     *
     * @param {ApiRequestPayload} payload - The JSON request payload
     *
     * @return {Promise}
     */
    private async json<T = any, S = never>({
        url,
        headers,
        signal,
        method,
        body,
    }: Omit<ApiRequestPayload, 'isJson'>): Promise<RequestResponse<T, S>> {
        return this.request({
            url,
            headers,
            method,
            ...(signal && { signal }),
            isJson: true,
            body,
        }) as Promise<RequestResponse<T, S>>;
    }

    /**
     * Make a GET API request to the server
     *
     * @param {GetPayload} payload - The GET request payload
     *
     * @return {Promise}
     */
    public async get<T = any, S = never>({
        url,
        headers,
        signal,
    }: Omit<GetPayload, 'method'>): Promise<RequestResponse<T, S>> {
        return this.json({
            url,
            headers,
            method: 'GET',
            ...(signal && { signal }),
        });
    }

    /**
     * Make a POST API request to the server
     *
     * @param {PostPayload} payload - The POST request payload
     *
     * @return {Promise}
     */
    public async post<T = any, S = never>({
        url,
        body,
        headers,
        signal,
    }: Omit<PostPayload, 'method'>): Promise<RequestResponse<T, S>> {
        return this.json({
            url,
            body,
            headers,
            method: 'POST',
            ...(signal && { signal }),
        });
    }

    /**
     * Make a PUT API request to the server
     *
     * @param {PutPayload} payload - The PUT request payload
     *
     * @return {Promise}
     */
    public async put<T = any, S = never>({
        url,
        body,
        headers,
    }: Omit<PutPayload, 'method'>): Promise<RequestResponse<T, S>> {
        return this.json({ url, body, headers, method: 'PUT' });
    }

    /**
     * Make a PATCH API request to the server
     *
     * @param {PatchPayload} payload - The PATCH request payload
     *
     * @return {Promise}
     */
    public async patch<T = any, S = never>({
        url,
        body,
        headers,
    }: Omit<PatchPayload, 'method'>): Promise<RequestResponse<T, S>> {
        return this.json({ url, body, headers, method: 'PATCH' });
    }

    /**
     * Make a DELETE API request to the server
     *
     * @param {DeletePayload} payload - The DELETE request payload
     *
     * @return {Promise}
     */
    public async delete<T = any, S = never>({
        url,
        headers,
    }: Omit<DeletePayload, 'method'>): Promise<RequestResponse<T, S>> {
        return this.json({ url, headers, method: 'DELETE' });
    }

    /**
     * Set the Authorization token header value
     *
     * @param {string} token - The auth token value
     */
    public setAuthorization(token: string) {
        this.emails = JWT.getUserEmails(token);
        this.headers = {
            ...this.headers,
            Authorization: `Bearer ${token}`,
        };
    }

    /**
     * Set the Tenant header value
     *
     * @param {string} tenant - The tenant value
     */
    public setTenant(tenant: string) {
        this.headers = {
            ...this.headers,
            Tenant: tenant,
        };
    }

    /**
     * Set the B2CAuthorization header value
     *
     * @param {string} p_name - Policy name
     */
    public setB2CAuthorization(policyName: string) {
        this.headers = {
            ...this.headers,
            B2CAuthorization: policyName,
        };
    }

    /**
     * Set the workspace id
     *
     * @param {string} workspaceId - workspace id
     */
    public setActiveWorkspace(workspaceId: string) {
        this.headers = {
            ...this.headers,
            Workspace: workspaceId,
        };
    }
}

const endpoint = getApiEndpoint();

export const api = new API(endpoint);
export * from './types';
